import { Auction, AuctionBid } from '@/api';
import { AnimateChangeInHeight } from '@/components/general/AnimateChangeInHeight';
import DateFormat from '@/components/general/DateFormat';
import EmptyState from '@/components/general/EmptyState';
import Price from '@/components/general/Price';
import useAuth from '@/contexts/AuthContext';
import useItemContext from '@/hooks/useItemContext';
import { getAuctionBidHistoryQueryFn } from '@/queries/auction';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import i18next from 'i18n';
import React, { useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

const DEFAULT_LIMIT = 1000; // ToDO: workaround, need to fix!!

const ProductBidHistory = () => {
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIMIT);
    const { item } = useItemContext();
    const [offset, setOffset] = useState(0);
    const { user } = useAuth();
    const [showMore, setShowMore] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['getAuctionBidHistoryQueryFn', item?.current_auction?.id as string, offset, itemsPerPage],
        queryFn: () => getAuctionBidHistoryQueryFn(item?.current_auction?.id as string, offset, itemsPerPage),
        enabled: item?.current_auction?.type === Auction.TypeEnum.Auction
    });

    const shouldShowEmptyState = useMemo(() => {
        if (!isLoading && data?.results?.length === 0) {
            return true;
        }
        return false;
    }, [data?.results?.length, isLoading]);

    if (item?.current_auction?.type !== Auction.TypeEnum.Auction) {
        return null;
    }
    const showReadMore = data?.results && data?.results?.length > 5;

    return (
        <div className={clsx('p-[20px] bg-grey16 rounded-[10px] w-full flex-col flex   max-w-full  sm-tablet:!max-w-full  tablet:!max-w-full  md:!max-w-[480px] relative', showReadMore && ' pb-[16px]')}>
            <AnimateChangeInHeight>
                <div className={clsx('relative')}>
                    <div className="flex justify-between items-center">
                        <h3 className="text-[20px] font-[500]">{i18next.t('product-bid.activity')}</h3>
                        <span className="block text-[10px] text-grey15 font-[500]">
                            {data?.pagination?.total} {i18next.t('item-text.bid-history.bids')} | {data?.amount_of_bidders} {i18next.t('item-text.bid-history.bidders')}
                        </span>
                    </div>
                    {isLoading &&
                        <div>
                            <Skeleton height={20} className="mt-3" />
                            <Skeleton height={20} className="mt-3" />
                            <Skeleton height={20} className="mt-3" />
                            <Skeleton height={20} className="mt-3" />
                            <Skeleton height={20} className="mt-3" />
                            <Skeleton height={20} className="mt-3" />
                        </div>
                    }
                    <div className=" flex flex-col-reverse" ref={ref}>
                        <div className={clsx('flex flex-col items-center  overflow-scroll  my-[16px] h-[258px]', showMore && '!h-[600px]')}>

                            {!isLoading && shouldShowEmptyState ?
                                EmptyState({
                                    title: i18next.t('item-text.bid-history.empty-state-title'),
                                    subtitle: i18next.t('item-text.bid-history.empty-state-subtitle'),
                                    actionText: i18next.t('item-text.bid-history.empty-state-action'),
                                    hasIcon: false,
                                    noPadding: true
                                })
                                :
                                !isLoading &&
                                <table className="w-full text-left  stickyTable">
                                    <thead className="text-grey15 capitalize text-[12px] font-[500]">
                                        <tr>
                                            <th className="w-[45%] sticky top-0 bg-grey16 md:w-[60%]">{i18next.t('item-text.bid-history.amount')}</th>
                                            <th className=" w-[100%] sticky bg-grey16 top-0">{i18next.t('item-text.bid-history.bid-time')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="">
                                        {data?.results?.map((bidItem: AuctionBid, i) => {
                                            return (
                                                <tr key={bidItem.id} className={clsx('border-b-[1px] border-grey14', bidItem.user_id === user?.id && 'font-[700] text-blue3')}>
                                                    <td className={clsx('text-[16px] font-[500] text-blue12 py-[10px]', i === 0 ? 'text-green4' : '')}>
                                                        <Price number={bidItem.amount} />
                                                    </td>
                                                    <td className="text-[14px] text-grey15">
                                                        <DateFormat date={bidItem.placed_at} customMask="mm/dd/yyyy hh:MM:ss tt" />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            }

                        </div>
                    </div>
                </div>

            </AnimateChangeInHeight>
            {showReadMore && <button onClick={() => setShowMore(!showMore)} className="absolute bottom-[16px] left-[20px] text-[14px] font-[500] text-blue16">{i18next.t('product-bid.show')} {showMore ? i18next.t('product-bid.less') : i18next.t('product-bid.more')}</button>}

        </div>
    );
};

export default ProductBidHistory;
